if (!customElements.get('lsg-accordion')) {
  class LSGAccordion extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        accordionItem: '.lsg-accordion-item',
        accordionTrigger: '.lsg-accordion-trigger',
        accordionContent: '.lsg-accordion-content',
      };

      this.settings = {
        triggerMob: this.hasAttribute('data-trigger-mob'),
        triggerTab: this.hasAttribute('data-trigger-tab'),
        triggerDesk: this.hasAttribute('data-trigger-desk'),
        closeOthers: this.hasAttribute('data-close-others'),
      };

      this.boundToggleAccordion = this._toggleAccordion.bind(this);
      this.boundResizeHandler = (window.LSGNHelpers && window.LSGNHelpers.debounce) 
        ? window.LSGNHelpers.debounce(this._handleResize.bind(this), 200)
        : this._handleResize.bind(this);
      this.boundOpenItemHandler = this._handleOpenItem.bind(this);
    }

    get elements() {
      return {
        accordionItems: this.querySelectorAll(this.classes.accordionItem),
      };
    }

    connectedCallback() {
      // Use setTimeout to ensure DOM is fully ready
      setTimeout(() => {
        this._init();
      }, 0);
      window.addEventListener('resize', this.boundResizeHandler);
      this.boundCloseAllHandler = this._closeAllAccordions.bind(this);
      this.addEventListener('lsg:accordion:close-all', this.boundCloseAllHandler);
      this.addEventListener('lsg:accordion:open-item', this.boundOpenItemHandler);
    }

    disconnectedCallback() {
      this._teardown();
      window.removeEventListener('resize', this.boundResizeHandler);
      this.removeEventListener('lsg:accordion:close-all', this.boundCloseAllHandler);
      this.removeEventListener('lsg:accordion:open-item', this.boundOpenItemHandler);
    }

    _init() {
      if (!this._shouldEnableAccordion()) {
        return;
      }

      const items = this.elements.accordionItems;
      
      if (items.length === 0) {
        return;
      }

      // First, ensure all accordion items are closed
      this._closeAllAccordions();

      // Then, open only items that have an explicit default-open-index
      items.forEach((item, index) => {
        const itemTrigger = item.querySelector(this.classes.accordionTrigger);
        if (itemTrigger) {
          itemTrigger.addEventListener('click', this.boundToggleAccordion);
        }
        const dataIndex = parseInt(item.getAttribute('data-index'), 10);
        const defaultIndex = parseInt(this.getAttribute('data-default-open-index'), 10);

        // Only open if there's an explicit default index set on the accordion element
        if (!isNaN(defaultIndex) && (defaultIndex === index || defaultIndex === dataIndex)) {
          this._openAccordion(item);
        }
      });
    }

    _teardown() {
      if (!this.elements || !this.elements.accordionItems) return;
      this.elements.accordionItems.forEach((item) => {
        const itemTrigger = item.querySelector(this.classes.accordionTrigger);
        if (itemTrigger) {
          itemTrigger.removeEventListener('click', this.boundToggleAccordion);
        }
      });
    }

    _handleResize() {
      this._teardown();
      this._init();
    }

    _shouldEnableAccordion() {
      const width = window.innerWidth;
      const { triggerMob, triggerTab, triggerDesk } = this.settings;
      if (!triggerMob && !triggerTab && !triggerDesk) return true;
      if (triggerMob && width <= 767) return true;
      if (triggerTab && width > 767 && width <= 1023) return true;
      if (triggerDesk && width > 1023) return true;
      return false;
    }

    _toggleAccordion(e) {
      e.preventDefault();
      e.stopPropagation();
      
      const item = e.currentTarget.closest(this.classes.accordionItem);
      if (!item) {
        return;
      }
      
      const isOpen = item.hasAttribute('data-expanded');
      const itemContent = item.querySelector(this.classes.accordionContent);
      const itemTrigger = item.querySelector(this.classes.accordionTrigger);

      if (!itemContent) {
        return;
      }

      if (isOpen) {
        itemContent.style.maxHeight = '0px';
        item.removeAttribute('data-expanded');
        item.classList.remove('is-open');
        if (itemTrigger) itemTrigger.setAttribute('aria-expanded', 'false');
      } else {
        if (this.settings.closeOthers) {
          this._closeAllAccordions();
        }
        this._openAccordion(item);
      }
    }

    _openAccordion(item) {
      const itemContent = item.querySelector(this.classes.accordionContent);
      const itemTrigger = item.querySelector(this.classes.accordionTrigger);
      if (!itemContent) return;
      item.setAttribute('data-expanded', 'true');
      item.classList.add('is-open');
      if (itemTrigger) itemTrigger.setAttribute('aria-expanded', 'true');
      const height = itemContent.scrollHeight;
      itemContent.style.maxHeight = `${height}px`;
    }

    _closeAllAccordions() {
      if (!this.elements || !this.elements.accordionItems) return;
      this.elements.accordionItems.forEach((item) => {
        const itemContent = item.querySelector(this.classes.accordionContent);
        const itemTrigger = item.querySelector(this.classes.accordionTrigger);
        if (itemContent) itemContent.style.maxHeight = '0px';
        if (itemTrigger) itemTrigger.setAttribute('aria-expanded', 'false');
        item.removeAttribute('data-expanded');
        item.classList.remove('is-open');
      });
    }

    _handleOpenItem(event) {
      if (!event.detail || !event.detail.index) return;

      const targetIndex = event.detail.index.toString();
      const targetItem = Array.from(this.elements.accordionItems).find((item) => {
        return item.getAttribute('data-index') === targetIndex;
      });

      if (targetItem) {
        if (this.settings.closeOthers) {
          this._closeAllAccordions();
        }
        this._openAccordion(targetItem);
      }
    }
  }

  window.customElements.define('lsg-accordion', LSGAccordion);
}